import { type DailyV2Response } from '@/api/schema/daily'
import { dayjs } from '@/api/utils/dayjs'
import { Col, Row } from '@/app/components/Flex/Flex'
import { Layout } from '@/app/components/Layout/Layout'
import { Loader } from '@/app/components/Loader/Loader'
import { Motion } from '@/app/components/Motion/Motion'
import { Text } from '@/app/components/Text/Text'
import { Lib } from '@/app/components/UI/Lib'
import { UI } from '@/app/components/UI/UI'
import { useBack } from '@/app/hooks/useBack'
import { useNavigate } from '@/app/hooks/useNavigate'
import DoSvg from '@/app/icons/do.svg?react'
import DontSvg from '@/app/icons/dont.svg?react'
import { Timeline } from '@/app/pages/DailyV2Page/Timeline/Timeline'
import { api } from '@/app/utils/api'
import type { FunctionalComponent } from 'preact'
import { useEffect, useMemo, useState } from 'preact/hooks'
import styles from './index.module.scss'

export const DailyV2Page: FunctionalComponent = () => {
  const back = useBack('~/tools')
  const route = useNavigate()

  const [data, setData] = useState<DailyV2Response | null>(null)
  const [reasonVisible, setReasonVisible] = useState(false)
  const hour = useMemo(() => dayjs().hour(), [])
  const generatedTime = useMemo(() => {
    if (!data?.generatedAt) {
      return null
    }

    return dayjs(data.generatedAt).format('HH:mm')
  }, [data?.generatedAt])

  useEffect(() => {
    const tz = dayjs.tz.guess()
    api.get<DailyV2Response>(`/daily-v2?tz=${tz}`)
      .then(resp => setData(resp.data))
  }, [])

  return (
    <Layout>
      {reasonVisible && data && (
        <UI.Popup
          onClose={() => {
            setReasonVisible(false)
          }}
        >
          <Lib.PopupHeader>
            Почему так?
          </Lib.PopupHeader>

          <Lib.PopupMessage>
            {data.personal.todayReason}
          </Lib.PopupMessage>

          <UI.Button
            size='m'
            type='primary'
            onClick={() => {
              setReasonVisible(false)
            }}
          >
            Окей
          </UI.Button>
        </UI.Popup>
      )}

      <Layout.Content
        onBack={back}
      >
        {!data
          ? (
            <Loader.Container>
              <Loader type='daily' />
            </Loader.Container>
          )
          : (
            <Motion>
              <Layout.Header>
                Твой дейлик
              </Layout.Header>

              <Row gap={15}>
                <UI.Panel
                  shadow
                  type='user'
                >
                  <Row gap={5}>
                    <Col gap={10} justifyContent='center'>
                      <Text fontWeight={500} fontSize={14} fontSizeMobile={13}>
                        {dayjs(data.date).format('D MMMM')}
                      </Text>
                      <Text fontWeight={500} fontSize={14} fontSizeMobile={13}>
                        {data.moon.emoji} Луна в {data.moon.prepositional}
                      </Text>
                    </Col>

                    <Text
                      fontSize={20}
                      fontWeight={600}
                      textAlign='center'
                    >
                      {hour >= 5 && hour < 12
                        ? (
                          <>
                            Доброе утро, {data.userName}
                          </>
                        )
                        : hour >= 12 && hour < 18
                        ? (
                          <>
                            Как твой день, {data.userName}?
                          </>
                        )
                        : hour >= 18 && hour < 23
                        ? (
                          <>
                            Добрый вечер, {data.userName}
                          </>
                        )
                        : (
                          <>
                            Доброй ночи, {data.userName}
                          </>
                        )}
                    </Text>

                    <UI.Holder maxWidth={420}>
                      <Text
                        fontSize={16}
                        textAlign='center'
                      >
                        {data.personal.teaser}
                      </Text>
                    </UI.Holder>
                  </Row>
                </UI.Panel>

                <UI.Panel>
                  <Row gap={10}>
                    <Col gap={10}>
                      <UI.Avatar size={40} bot='zoe' />
                      <Row gap={2}>
                        <Text
                          fontSize={16}
                          fontWeight={600}
                        >
                          Зоя
                        </Text>
                        <Text fontSize={13} opacity={0.5}>
                          Астролог • {generatedTime}
                        </Text>
                      </Row>
                    </Col>

                    <Text
                      fontSize={16}
                      lineHeight={1.3}
                    >
                      {data.personal.message}
                    </Text>

                    <div>
                      <UI.Button
                        type='secondary'
                        size='s'
                        onClick={() => {
                          setReasonVisible(true)
                        }}
                      >
                        Почему так?
                      </UI.Button>
                    </div>
                  </Row>
                </UI.Panel>

                <UI.Panel>
                  <Row gap={20}>
                    <Row gap={10}>
                      <Text upper fontSize={13} fontWeight={600}>
                        Сегодня лучше
                      </Text>
                      <Row gap={10}>
                        {data.personal.doList.map(item => (
                          <Col gap={8} alignItems='flex-start'>
                            <DoSvg width={20} height={20} class={styles.do} />
                            <Text fontSize={16}>{item}</Text>
                          </Col>
                        ))}
                      </Row>
                    </Row>

                    <Row gap={10}>
                      <Text upper fontSize={13} fontWeight={600}>
                        Не стоит
                      </Text>
                      <Row gap={10}>
                        {data.personal.dontList.map(item => (
                          <Col gap={8} alignItems='flex-start'>
                            <DontSvg width={20} height={20} class={styles.dont} />
                            <Text fontSize={16}>{item}</Text>
                          </Col>
                        ))}
                      </Row>
                    </Row>
                  </Row>
                </UI.Panel>

                <Timeline timeline={data.timeline} />

                <UI.Button
                  size='l'
                  type='primary'
                  onClick={() => {
                    const q = 'На что стоит обратить внимание в ближайшие 7 дней? Расскажи подробно.'
                    route(`~/chats/zoe?q=${encodeURIComponent(q)}`)
                  }}
                >
                  Прогноз на 7 дней
                </UI.Button>
              </Row>
            </Motion>
          )}
      </Layout.Content>
    </Layout>
  )
}
